import { useState, useMemo, useEffect } from "react";
import { getPeerReviewData, acceptPeerReview } from "./analystTaskApi";
import Spinner from "react-bootstrap/Spinner";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import { formatDate } from "../../../utility/Formatdate";
import "../../../styles/typography.css";
import SecondTab from "../../../components/organisms/secondTab";
import { LabAnalystTabConfig } from "../LabAnalyst.constants";
import { toast, ToastContainer } from "react-toastify";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import { BsCircleFill } from "react-icons/bs";

const getStatusIcon = (row) => {
  return (
    <>
      {row.isGreaterThanTen && (
        <BsCircleFill
          color="#43B324"
          size={10}
          title="Due > 10"
          className="me-1"
        />
      )}
      {row.isGreaterThanFourteen && (
        <BsCircleFill
          color="#E13B3B"
          size={10}
          title="Due > 14"
          className="me-1"
        />
      )}
    </>
  );
};

const AnalystPeerReview = ({ TabConfig }) => {
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditComment, setAuditComment] = useState("");
  const [isAcceptClicked, setIsAcceptClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState({});
  const tabButtons = [
    { label: "My Assignments", path: "/analyst-tasks/myassignment" },
    { label: "Peer Review", path: "/analyst-tasks/peer-review" },
  ];

  useEffect(() => {
    fetchPeerReview();
  }, [currentPage, rowsPerPage, selectedCompany, selectedProduct]);

  const fetchPeerReview = async (search="", currentPage=1, rowsPerPage=25) => {
    setLoading(true);
    try {
      const payload = {
        companyId: selectedCompany ? Number(selectedCompany) : 0,
        productId: selectedProduct ? Number(selectedProduct) : 0,
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        status: "",
      };
      const res = await getPeerReviewData(payload);
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setData(Array.isArray(res.results) ? res.results : []);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAccept = async (isApproved) => {
    if (!isAnySelected) return;
    // Build payload array for selected rows
    const peerReviewRequestSample = Array.from(selectedRowIds).map(
      (sampleId) => {
        const row = data.find((d) => d.sampleId === sampleId);
        if (row) {
          return {
            sampleId: row.sampleId,
            assayResult: row.assay || "",
            reAssay1Result: row.reAssay1 || "",
            reAssay2Result: row.reAssay2 || "",
            comments: auditComment || "",
            nextStatus: row.nextStatus || "",
          };
        }
      }
    );
    const payload = {
      peerReviewRequestSample: peerReviewRequestSample.filter((ele) => ele),
      isApproved: isApproved,
    };
    try {
      const res = await acceptPeerReview(payload);
      if (res) {
        toast.success("Samples accepted successfully");
        setShowAuditModal(false);
        // Fetch table data again
        const params = {
          PageNumber: currentPage,
          PageSize: rowsPerPage,
          Company: selectedCompany,
          Product: selectedProduct,
        };
        const refreshed = await getPeerReviewData(params);
        setData(Array.isArray(refreshed.results) ? refreshed.results : []);
      } else {
        toast.error("Failed to accept samples");
      }
    } catch (error) {
      toast.error("Failed to accept samples");
    }
  };

  const handleAcceptOrRemoveClick = (actionType) => {
    setIsAcceptClicked(actionType === "Accept");
    setShowAuditModal(true);
  };

  const companyOptions = useMemo(() => {
    const seen = new Set();
    const uniqueCompanies = data
      .filter(
        (row) => row.company && !seen.has(row.company) && seen.add(row.company)
      )
      .map((row) => ({ value: row.companyId, label: row.company }));
    uniqueCompanies.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 0, label: "All" }, ...uniqueCompanies];
  }, [data]);

  const productOptions = useMemo(() => {
    const seen = new Set();
    const uniqueProducts = data
      .filter(
        (row) => row.product && !seen.has(row.product) && seen.add(row.product)
      )
      .map((row) => ({ value: row.productId, label: row.product }));
    uniqueProducts.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 0, label: "All" }, ...uniqueProducts];
  }, [data]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return data.slice(startIdx, startIdx + rowsPerPage);
  }, [currentPage, rowsPerPage, data]);

  const currentPath = window.location.pathname;
  const isAnySelected = selectedRowIds.size > 0;
  const isAllSelectedOnPage =
    paginatedData.length > 0 &&
    paginatedData.every((row) => selectedRowIds.has(row.sampleId));

  const toggleSelectAllOnPage = () => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (!isAllSelectedOnPage) {
        paginatedData.forEach((row) => copy.add(row.sampleId));
      } else {
        paginatedData.forEach((row) => copy.delete(row.sampleId));
      }
      return copy;
    });
  };

  const columns = [
    {
      header: (
        <Form.Check
          type="checkbox"
          checked={isAllSelectedOnPage}
          onChange={toggleSelectAllOnPage}
          aria-label="Select all"
        />
      ),
      accessor: "checkbox",
      disableSort: true,
      cell: (row) => (
        <Form.Check
          type="checkbox"
          checked={selectedRowIds.has(row.sampleId)}
          onChange={() => {
            const newSet = new Set(selectedRowIds);
            if (newSet.has(row.sampleId)) newSet.delete(row.sampleId);
            else newSet.add(row.sampleId);
            setSelectedRowIds(newSet);
          }}
        />
      ),
    },
  {
      header: "Sample ID",
      accessor: "sampleId",
      cell: (row) => (
        <span>
          {row.sampleId} {getStatusIcon(row)}
        </span>
      ),
    },
    { header: "Product", accessor: "product" },
    { header: "Company", accessor: "company" },
    { header: "Date", accessor: "date", cell: (row) => formatDate(row.date) },
    { header: "Unit", accessor: "unit" },
    {
      header: "Exp Level",
      accessor: "expLevel",
      cell: (row) => row.expLevel.toString(),
    },
    {
      header: "Low Limit",
      accessor: "lowLimit",
      cell: (row) => row.lowLimit.toString(),
    },
    {
      header: "Up Limit",
      accessor: "upLimit",
      cell: (row) => row.upLimit.toString(),
    },
    {
      header: "Assay",
      accessor: "assay",
      cell: (row) => {
        // only editable if both reAssay1 and reAssay2 empty
        const canEditAssay =
          (!row.reAssay1 || Number(row.reAssay1) === 0) &&
          (!row.reAssay2 || Number(row.reAssay2) === 0) &&
          selectedRowIds.has(row.sampleId);

        const displayValue =
          row.assay === undefined || row.assay === null || row.assay === ""
            ? "-"
            : Number(row.assay).toFixed(2);

        if (canEditAssay) {
          return (
            <Form.Control
              size="sm"
              type="text"
              value={row.assay ?? ""}
              onChange={(e) =>
                setData((prev) =>
                  prev.map((r) =>
                    r.sampleId === row.sampleId
                      ? { ...r, assay: e.target.value }
                      : r
                  )
                )
              }
            />
          );
        }

        return <span>{displayValue}</span>;
      },
    },
    { header: "Assay by", accessor: "assayBy" },

    {
      header: "Re-Assay1",
      accessor: "reAssay1",
      cell: (row) => {
        // editable only if reAssay1 exists and reAssay2 empty
        const canEditReAssay1 =
          row.reAssay1 &&
          Number(row.reAssay1) !== 0 &&
          (!row.reAssay2 || Number(row.reAssay2) === 0) &&
          selectedRowIds.has(row.sampleId);

        const displayValue =
          row.reAssay1 === undefined ||
          row.reAssay1 === null ||
          row.reAssay1 === "" ||
          Number(row.reAssay1) === 0
            ? "-"
            : Number(row.reAssay1).toFixed(2);

        if (canEditReAssay1) {
          return (
            <Form.Control
              size="sm"
              type="text"
              value={row.reAssay1 ?? ""}
              onChange={(e) =>
                setData((prev) =>
                  prev.map((r) =>
                    r.sampleId === row.sampleId
                      ? { ...r, reAssay1: e.target.value }
                      : r
                  )
                )
              }
            />
          );
        }

        return <span>{displayValue}</span>;
      },
    },
    { header: "Re-Assay1 by", accessor: "reAssay1By" },

    {
      header: "Re-Assay2",
      accessor: "reAssay2",
      cell: (row) => {
        // editable only if reAssay2 exists
        const canEditReAssay2 =
          row.reAssay2 &&
          Number(row.reAssay2) !== 0 &&
          selectedRowIds.has(row.sampleId);

        const displayValue =
          row.reAssay2 === undefined ||
          row.reAssay2 === null ||
          row.reAssay2 === "" ||
          Number(row.reAssay2) === 0
            ? "-"
            : Number(row.reAssay2).toFixed(2);

        if (canEditReAssay2) {
          return (
            <Form.Control
              size="sm"
              type="text"
              value={row.reAssay2 ?? ""}
              onChange={(e) =>
                setData((prev) =>
                  prev.map((r) =>
                    r.sampleId === row.sampleId
                      ? { ...r, reAssay2: e.target.value }
                      : r
                  )
                )
              }
            />
          );
        }

        return <span>{displayValue}</span>;
      },
    },

    { header: "Re-Assay2 by", accessor: "reAssay2By" },
    { header: "Result", accessor: "result" },
    {
      header: "Status",
      accessor: "nextStatus",
    },
  ];

  const navigateTo = (path) => {
    window.location.href = path;
  };

  return (
    <>
      <SecondTab tabConfig={TabConfig} />

      <div className="p-3 typography">
        <div
          className="d-flex shadow-lg mb-2 align-items-center p-3"
          style={{ background: "#fff", borderRadius: "5px" }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            {tabButtons.map((tab) => (
              <Button
                key={tab.path}
                variant={currentPath.endsWith(tab.path) ? "active" : "outlined"}
                onClick={() => navigateTo(tab.path)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            <label style={{ fontSize: 14, marginRight: 4 }}>Company</label>
            <select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                minWidth: 120,
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              {companyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label
              style={{
                fontSize: 14,
                marginLeft: 12,
                marginRight: 4,
              }}
            >
              Product
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                minWidth: 120,
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              {productOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div
              className="line-separator"
              style={{
                width: "1px",
                height: "46px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            />
            <Button
              variant={isAnySelected ? "outlined" : "disabled"}
              disabled={!isAnySelected}
              onClick={() => handleAcceptOrRemoveClick("Reject")}
            >
              Reject
            </Button>
            <Button
              variant={isAnySelected ? "outlined" : "disabled"}
              disabled={!isAnySelected}
              style={{ marginLeft: 8 }}
              onClick={() => handleAcceptOrRemoveClick("Accept")}
            >
              Accept
            </Button>
          </div>
        </div>
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spinner animation="border" /> Loading...
                </div>
              ) : (
                <>
                  <SimpleTable
                    columns={columns}
                    data={paginatedData}
                    showPagination={true}
                    paginationData={paginationData}
                    searchValue={""}
                    fetchData={fetchPeerReview}
                  />
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <ReusableModal
        show={showAuditModal}
        onHide={() => setShowAuditModal(false)}
        title="Confirm"
        size="md"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setShowAuditModal(false),
          },
          {
            text: isAcceptClicked ? "Ok" : "Update",
            variant: "active",
            onClick: () =>
              isAcceptClicked
                ? handleConfirmAccept(true)
                : handleConfirmAccept(false),
          },
        ]}
      >
        {!isAcceptClicked && (
          <div>
            <p>
              Are you sure you want to <b>Reject</b> the value? If yes, please
              provide a comment for this change. It will be recorded in the
              audit trail.
            </p>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Comments..."
              value={auditComment}
              onChange={(e) => setAuditComment(e.target.value)}
            />
          </div>
        )}
        {isAcceptClicked && (
          <div>
            <p>
              Are you sure you want to <b>Accept</b> the value? If yes, please
              provide a comment for this change. It will be recorded in the
              audit trail.
            </p>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Comments..."
              value={auditComment}
              onChange={(e) => setAuditComment(e.target.value)}
            />
          </div>
        )}
      </ReusableModal>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default AnalystPeerReview;
